import { Block } from '@/components/base/Block'
import { PrimaryButton, SubButton } from '@/components/base/Button'
import FavoriteIcon from '@/components/base/Icon/FavoriteIcon'
import { Text4 } from '@/components/base/Text'
import { useApp } from '@/hooks/useApp'
import useIsMobile from '@/hooks/useMobile'
import { borderLine, borderSize } from '@/styles/border'
import borderRadius from '@/styles/border_radius'
import { align, display } from '@/styles/display'
import { fontSize, fontWeight } from '@/styles/font'
import spaceStyle from '@/styles/space'
import styled from 'styled-components'

const CardContainer = styled.div<{ $bgColor: string }>`
  background-color: ${(props) => props.$bgColor};
  border-radius: ${borderRadius.L};
  border: ${borderSize.S} ${borderLine.Solid} #e0e0e0;
  padding: ${spaceStyle.M};
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 767px) {
    padding: ${spaceStyle.S};
  }

  @media (min-width: 768px) {
    max-width: 700px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${spaceStyle.S};
  flex-wrap: nowrap;

  @media (max-width: 767px) {
    gap: ${spaceStyle.XS};
  }
`

const CatchphraseText = styled.div`
  font-size: ${fontSize.XL};
  font-weight: ${fontWeight.Bold};
  color: #000000;
  margin-bottom: ${spaceStyle.M};

  @media (max-width: 767px) {
    font-size: ${fontSize.L};
    margin-bottom: ${spaceStyle.S};
  }
`

const JobIdContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spaceStyle.XXS};
  flex-wrap: wrap;
  margin-bottom: ${spaceStyle.XS};
  font-size: ${fontSize.M};
  line-height: 1;
  row-gap: 0px;

  @media (max-width: 767px) {
    font-size: ${fontSize.S};
    gap: 2px;
    margin-bottom: ${spaceStyle.XXS};
    line-height: 1;
    row-gap: 0px;
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 2px solid #e0e0e0;
  margin: ${spaceStyle.XS} 0;

  @media (max-width: 767px) {
    margin: ${spaceStyle.XXS} 0;
  }
`

const InfoRow = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: ${spaceStyle.XS};
  gap: ${spaceStyle.XS};

  @media (max-width: 767px) {
    margin-bottom: 4px;
    gap: 4px;
  }
`

const InfoLabel = styled.span`
  width: 88px;
  color: #666;
  font-size: ${fontSize.M};
  white-space: pre;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 70px;
    font-size: ${fontSize.S};
  }
`

const InfoText = styled.div`
  color: #333;
  font-size: ${fontSize.M};
  flex: 1;
  text-align: left;

  @media (max-width: 767px) {
    font-size: ${fontSize.S};
  }
`

const AdviceBox = styled.div`
  margin-top: ${spaceStyle.M};
  margin-bottom: ${spaceStyle.M};
  font-size: ${fontSize.M};
  white-space: pre-wrap;

  @media (max-width: 767px) {
    margin-top: ${spaceStyle.S};
    margin-bottom: ${spaceStyle.S};
    font-size: ${fontSize.S};
  }
`

interface Label {
  text: string
  appThemeType: string
}

interface AttachmentInfo {
  title: string
  text: string
  iconType?: string
}

interface Catchphrase {
  text: string
}

interface JobPostingCardType4Props {
  // API Response data
  id: string
  uuid: string
  catchphrases?: Catchphrase[]
  labels?: Label[]
  shopName?: string
  attachmentInfo?: AttachmentInfo[]
  adviserAdvice?: string

  // 機能制御
  onClickDetail: (id: string) => void
  onClickInquiry: (id: string) => void
  onClickFavorite?: (id: string, newStatus: boolean) => void

  // 問い合わせ可能フラグ
  isInquiry?: boolean

  // お気に入り機能
  isShowFavoriteButton?: boolean
  isLiked?: boolean

  // ソース情報（AIチャット経由かどうかなど）
  source?: string
}

const JobPostingCardType4 = ({
  id,
  uuid,
  catchphrases,
  labels,
  shopName,
  attachmentInfo,
  adviserAdvice,
  onClickDetail,
  onClickInquiry,
  onClickFavorite,
  isInquiry = true,
  isShowFavoriteButton = false,
  isLiked = false,
  source
}: JobPostingCardType4Props): JSX.Element => {
  const app = useApp()
  const isMobile = useIsMobile()

  const handleDetailClick = (): void => {
    onClickDetail(id)
  }

  const handleInquiryClick = (): void => {
    if (isInquiry) {
      onClickInquiry(id)
    }
  }

  const handleFavoriteClick = (): void => {
    if (onClickFavorite !== undefined) {
      onClickFavorite(id, !isLiked)
    }
  }

  // キャッチフレーズを取得
  const catchphrase = catchphrases?.[0]?.text ?? ''

  return (
    <CardContainer $bgColor={app.theme.whiteColor.color ?? '#FFFFFF'}>
      {/* キャッチフレーズ */}
      {catchphrase !== '' && <CatchphraseText>{catchphrase}</CatchphraseText>}

      {/* 求人ID・雇用形態・施設名 */}
      <JobIdContainer>
        <Text4
          margin={{ all: spaceStyle.NONE }}
          fontSize={isMobile ? fontSize.S : fontSize.M}
        >
          求人ID.
        </Text4>
        <Text4
          margin={{ all: spaceStyle.NONE }}
          fontSize={isMobile ? fontSize.S : fontSize.M}
        >
          {uuid}
        </Text4>
        {labels?.map((label, index) => (
          <Text4
            key={index}
            margin={{ all: spaceStyle.NONE }}
            fontSize={isMobile ? fontSize.S : fontSize.M}
          >
            {label.text}
          </Text4>
        ))}
        {shopName !== undefined && shopName !== '' && (
          <Text4
            margin={{ all: spaceStyle.NONE }}
            fontSize={isMobile ? fontSize.S : fontSize.M}
          >
            {shopName}
          </Text4>
        )}
      </JobIdContainer>

      <Divider />

      {/* 求人情報 */}
      <Block margin={{ bottom: isMobile ? spaceStyle.S : spaceStyle.M }}>
        {/* attachmentInfoの内容を格納順に表示 */}
        {attachmentInfo?.map((info, index) => (
          <InfoRow key={index}>
            <InfoLabel>{info.title}</InfoLabel>
            <InfoText>
              {info.text.split('\n').map((line, lineIndex) => (
                <Text4
                  key={lineIndex}
                  textColor={app.theme.subTextColor}
                  margin={{ all: spaceStyle.NONE }}
                  fontSize={isMobile ? fontSize.S : fontSize.M}
                >
                  {line}
                </Text4>
              ))}
            </InfoText>
          </InfoRow>
        ))}
      </Block>

      {/* アドバイザーアドバイス */}
      {adviserAdvice !== undefined && adviserAdvice !== '' && (
        <AdviceBox>
          <Text4
            textColor={app.theme.subTextColor}
            margin={{ all: spaceStyle.NONE }}
            fontSize={isMobile ? fontSize.S : fontSize.M}
          >
            {adviserAdvice}
          </Text4>
        </AdviceBox>
      )}

      {/* ボタンエリア（スマホではボタン文字を小さくして「お問い合わせ済み」のはみ出しを防止） */}
      <ButtonContainer>
        {/* 詳細を見るボタン */}
        <Block
          width={isMobile ? 'auto' : '200px'}
          style={isMobile ? { flex: 1, minWidth: 0 } : undefined}
        >
          <SubButton
            onClick={handleDetailClick}
            width="100%"
            fontSize={isMobile ? fontSize.XS : undefined}
          >
            詳細を見る
          </SubButton>
        </Block>
        {/* お問い合わせボタン */}
        <Block
          width={isMobile ? 'auto' : '200px'}
          style={isMobile ? { flex: 1, minWidth: 0 } : undefined}
        >
          <PrimaryButton
            disableClickAction={!isInquiry}
            onClick={handleInquiryClick}
            width="100%"
            fontSize={isMobile ? fontSize.XS : undefined}
          >
            {isInquiry ? 'お問い合わせ' : 'お問い合わせ済み'}
          </PrimaryButton>
        </Block>
        {/* お気に入りボタン */}
        {isShowFavoriteButton && (
          <Block
            width={isMobile ? 'auto' : '60px'}
            display={display.FLEX}
            alignItems={align.CENTER}
            style={{ justifyContent: 'center', flexShrink: 0 }}
          >
            <FavoriteIcon
              id={id}
              paddingProp={{ all: spaceStyle.NONE }}
              isLiked={isLiked}
              onFavoriteChange={handleFavoriteClick}
              source={source}
            />
          </Block>
        )}
      </ButtonContainer>
    </CardContainer>
  )
}

export default JobPostingCardType4
